import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { API, authFetch } from "@/App";
import {
  Bell, Check, Trash2, AlertTriangle, CreditCard, Users,
  Target, Handshake, Clock, X
} from "lucide-react";

const TYPE_CONFIG = {
  overdue_invoice: { icon: CreditCard, color: "text-red-500", bg: "bg-red-50" },
  inactive_client: { icon: Users, color: "text-amber-500", bg: "bg-amber-50" },
  high_score_lead: { icon: Target, color: "text-emerald-500", bg: "bg-emerald-50" },
  partnership_reply: { icon: Handshake, color: "text-blue-500", bg: "bg-blue-50" },
  expiring_subscription: { icon: Clock, color: "text-purple-500", bg: "bg-purple-50" },
  onboarding_complete: { icon: Check, color: "text-green-500", bg: "bg-green-50" },
};

export function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/notifications`);
      if (res.ok) {
        const d = await res.json();
        setNotifications(d.notifications || []);
        setUnread(d.unread_count || 0);
      }
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAllRead = async () => {
    await authFetch(`${API}/notifications/mark-read`, { method: "POST", body: JSON.stringify({}) });
    fetchNotifications();
  };

  const deleteNotif = async (id) => {
    await authFetch(`${API}/notifications/${id}`, { method: "DELETE" });
    fetchNotifications();
  };

  return (
    <>
      <button onClick={() => { setOpen(true); if (unread > 0) markAllRead(); }}
        className="relative p-2 rounded-lg hover:bg-neutral-100 transition-colors" data-testid="notification-bell">
        <Bell className="w-5 h-5 text-neutral-600" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="p-4 border-b flex-row items-center justify-between">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Bell className="w-4 h-4 text-[#2BC40E]" /> Notifications
            </DialogTitle>
            {notifications.length > 0 && (
              <Button variant="ghost" size="sm" className="text-xs text-neutral-500" onClick={markAllRead}>
                Mark all read
              </Button>
            )}
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="text-center py-12 text-neutral-500">
                <Bell className="w-10 h-10 mx-auto mb-2 opacity-20" />
                <p className="text-sm">All caught up!</p>
              </div>
            ) : (
              <div className="divide-y divide-neutral-100">
                {notifications.map(n => {
                  const config = TYPE_CONFIG[n.type] || { icon: Bell, color: "text-neutral-500", bg: "bg-neutral-50" };
                  const Icon = config.icon;
                  return (
                    <div key={n.notification_id} className={`p-3.5 flex gap-3 ${!n.read ? "bg-blue-50/30" : ""}`}>
                      <div className={`w-8 h-8 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-4 h-4 ${config.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-neutral-900">{n.title}</p>
                        <p className="text-xs text-neutral-500 mt-0.5">{n.message}</p>
                        <p className="text-[10px] text-neutral-400 mt-1">{n.created_at?.slice(0, 16).replace("T", " ")}</p>
                      </div>
                      <button onClick={() => deleteNotif(n.notification_id)} className="text-neutral-300 hover:text-red-400 p-1 flex-shrink-0">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default NotificationBell;
